import { useEffect, useRef } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { useHistory, useLocation } from "react-router";
import { useSelector } from "react-redux";
import { isFullAuth } from "../../services/RouteAuth";
import {
  FREE_PROGRAM_INTERNAL_PATH,
  clearPendingCommunityPath,
  isJourneysPath,
  normalizeDietaSocialUrl,
  readPendingCommunityPath,
  savePendingCommunityPath,
} from "../../utils/communityDeepLinks";

import {
  claimNativeLaunchLink,
  rememberNativeLink,
} from "../../utils/nativeLaunchLink";

const DUPLICATE_WINDOW_MS = 1500;
// Where a new member is offered the free week at the end of onboarding
// (WeHaveFinished → /percorso-gratuito). A link to the Percorsi tab waits there
// and opens once they move on (client, 08/10/2026: the free-week offer first,
// then the percorso).
const FREE_OFFER_PATHS = ["/onboarding-complete", FREE_PROGRAM_INTERNAL_PATH];

const CommunityDeepLinkHandler = () => {
  const history = useHistory();
  const location = useLocation();
  const lastHandledRef = useRef({ path: null, at: 0 });
  const token = useSelector((state) => state.user?.token);
  const userId = useSelector((state) => state.user?.user?.id);
  const subscriberId = useSelector(
    (state) => state.user?.master_club_subscriber?.id,
  );

  useEffect(() => {
    let disposed = false;
    let listenerHandle;
    let receivedLiveUrl = false;

    const handleExternalUrl = (rawUrl) => {
      const path = normalizeDietaSocialUrl(rawUrl);
      if (!path) return;

      const now = Date.now();
      if (
        lastHandledRef.current.path === path &&
        now - lastHandledRef.current.at < DUPLICATE_WINDOW_MS
      ) {
        return;
      }
      lastHandledRef.current = { path, at: now };
      rememberNativeLink(path);

      savePendingCommunityPath(path, now);
      if (isFullAuth()) {
        clearPendingCommunityPath();
      }
      history.push(path);
    };

    CapacitorApp.addListener("appUrlOpen", ({ url }) => {
      if (!disposed) {
        receivedLiveUrl = true;
        handleExternalUrl(url);
      }
    }).then((handle) => {
      listenerHandle = handle;
      if (disposed) handle.remove();
    });

    CapacitorApp.getLaunchUrl()
      .then((launch) => {
        if (disposed) return;
        const isFresh = claimNativeLaunchLink(normalizeDietaSocialUrl(launch?.url));
        // A newer URL event wins over a delayed startup snapshot.
        if (!receivedLiveUrl && isFresh) handleExternalUrl(launch.url);
      })
      .catch(() => {});

    return () => {
      disposed = true;
      listenerHandle?.remove();
    };
  }, [history]);

  // RouteAuth intentionally sends signed-out users to login. Once the normal
  // auth bootstrap reaches a full profile, replay the one-time external target
  // instead of losing it to the login redirect.
  useEffect(() => {
    if (!isFullAuth()) return;
    const pendingPath = readPendingCommunityPath();
    if (!pendingPath) return;
    if (FREE_OFFER_PATHS.includes(location.pathname)) {
      if (isJourneysPath(pendingPath)) return;
      // The free-week link (/app?sezione=gratis) at the end of onboarding: the
      // onboarding brings a new member to that page itself, with the «Esplora
      // l'app senza iscriverti» way out a later visit doesn't offer, or to
      // their course / the Home when there's nothing to enrol in.
      if (pendingPath === FREE_PROGRAM_INTERNAL_PATH) {
        clearPendingCommunityPath();
        return;
      }
    }
    clearPendingCommunityPath();
    const currentPath = `${location.pathname}${location.search || ""}`;
    if (currentPath !== pendingPath) {
      history.replace(pendingPath);
    }
  }, [
    history,
    location.pathname,
    location.search,
    subscriberId,
    token,
    userId,
  ]);

  return null;
};

export default CommunityDeepLinkHandler;
